import React, { useEffect, useState } from 'react';
import {
  Button,
  Modal,
  Row,
  Col,
  Typography,
  Card,
  Image,
  Checkbox,
  Badge,
  Divider,
  List,
  Collapse,
  Input,
  Slider,
  Select,
  UploadFile,
} from 'antd';
import { useQuery } from '@apollo/client';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import { queries } from '../../../graphql/graphql';
import { FileGrid } from '../../Form/FileGrid';
import {
  FluxExteriorProps,
  FluxGenerationsViewProps,
  FluxGenerationsProps,
  Generation,
  FluxPhoto,
} from '../../../types/flux';
import { stylesList } from '../../../consts/fluxStyleList';
const { Title, Text } = Typography;
const { Meta } = Card;
const { Panel } = Collapse;

const FluxGenerationsView: React.FC<FluxGenerationsViewProps> = ({ items = [], onAdd }) => {
  const calculatePendingTime = (generations: Generation[]): number => {
    let time = 0;
    for (const item of generations) {
      if (item.status !== 'done') {
        try {
          time += Number(item.data.settings.steps * item.data.results.length);
        } catch (error) {
          console.error(error);
        }
      }
    }
    return Math.ceil(time / 60);
  };

  return (
    <Row gutter={[16, 16]} style={{ width: '100%' }}>
      <Col span={24}>
        {items.length > 0 && (
          <>
            <Text>
              Done: {items.filter(item => item.status === 'done').length}; Execution time:{' '}
              {Number(items.reduce((acc, item) => acc + +item.executionTime, 0) / 60 / 60).toFixed(
                1
              )}
              h
            </Text>
            <br />
            <Text>
              Pending: {items.filter(item => item.status !== 'done').length}; Remaining:{' '}
              {calculatePendingTime(items)}m
            </Text>
          </>
        )}
      </Col>
      <List
        pagination={{
          pageSize: 20,
        }}
        style={{ width: '100%' }}
        dataSource={items}
        renderItem={(generation, genIndex) => (
          <Col span={24} key={`gen-${genIndex}`}>
            <Badge.Ribbon
              text={generation.status}
              color={generation.status === 'done' ? 'green' : 'yellow'}>
              <Card style={{ width: '100%' }}>
                <Row gutter={[16, 16]}>
                  <Col span={24}>
                    <Card>
                      <Image style={{ maxWidth: '400px' }} src={generation.sourceImage} />
                      <Meta
                        title={`Execution time: ${generation.executionTime}s`}
                        description={
                          <div>
                            <p>Settings:</p>
                            <p>steps: {generation.data.settings.steps}</p>
                            <p>guidance: {generation.data.settings.guidance}</p>
                            <p>
                              control type:{' '}
                              {generation.data.settings.control_type === 2 ? 'depth' : 'canny'}
                            </p>
                            <p>control scale: {generation.data.settings.control_scale}</p>
                            <p>
                              control guidance start:{' '}
                              {generation.data.settings.control_guidance_start}
                            </p>
                            <p>
                              control guidance end: {generation.data.settings.control_guidance_end}
                            </p>
                          </div>
                        }
                      />
                    </Card>
                  </Col>
                  <Col span={24}>
                    <Collapse>
                      <Panel
                        key={`panel-${genIndex}`}
                        header={`Results [${generation.data.results.length}]`}
                        disabled={generation.status !== 'done'}>
                        <Row gutter={[16, 16]}>
                          {generation.data.results.map((result, resIndex) => (
                            <Col span={24} key={`result-${resIndex}`}>
                              <List
                                itemLayout="vertical"
                                size="large"
                                dataSource={result.images}
                                renderItem={(item, index) => (
                                  <List.Item key={index}>
                                    <Row gutter={[0, 16]}>
                                      <Col span={12}>
                                        <Image alt="source" src={generation.sourceImage} />
                                      </Col>
                                      <Col span={12}>
                                        <Image alt="generated" src={item} />
                                      </Col>
                                      <Col span={24}>
                                        <Text>Prompt: </Text>
                                        <Text type="secondary">
                                          {result.prompt}
                                          <br />
                                        </Text>
                                        <Text>Styles: </Text>
                                        <Text type="secondary">
                                          {result.styles.join(', ')}
                                          <br />
                                        </Text>
                                      </Col>
                                      {onAdd && (
                                        <Col span={24}>
                                          <Button
                                            type="primary"
                                            onClick={() => onAdd(item)}
                                            key="preview">
                                            Add
                                          </Button>
                                        </Col>
                                      )}
                                    </Row>
                                  </List.Item>
                                )}
                              />
                              <Divider />
                            </Col>
                          ))}
                        </Row>
                      </Panel>
                    </Collapse>
                  </Col>
                </Row>
              </Card>
            </Badge.Ribbon>
          </Col>
        )}
      />
    </Row>
  );
};

const FluxGenerations: React.FC<FluxGenerationsProps> = ({ id, onAdd, loadingGenerate }) => {
  const {
    data = {},
    refetch,
    loading,
  } = useQuery(queries.flux.FLUX_GENERATIONS_BY_PLAN_ID, {
    variables: { planId: id },
  });

  useEffect(() => {
    refetch();
  }, [loadingGenerate, refetch]);

  if (loading) {
    return <Typography.Text>Loading...</Typography.Text>;
  }

  if (!data.fluxGenerationByPlanId) {
    return <Typography.Text>Oops...</Typography.Text>;
  }

  return <FluxGenerationsView items={data.fluxGenerationByPlanId} onAdd={onAdd} />;
};

const FluxExterior: React.FC<FluxExteriorProps> = ({ value = [], planId, onChange }) => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [controlnetImage, setControlnetImage] = useState<UploadFile>();
  const [styles, setStyles] = useState<string[]>([
    'blue_wood',
    'white_painted',
    'white_wood',
    'green_wood',
    'combined_white_wood',
    'yellow_wood',
    'hip_roof',
  ]);
  const [loadingGenerate, setLoadingGenerate] = useState<boolean>(false);
  const [steps, setSteps] = useState<number>(28);
  const [controlScale, setControlScale] = useState<number>(0.6);
  const [controlType, setControlType] = useState<number>(2);
  const [guidance, setGuidance] = useState<number>(3.5);
  const [controlGuidanceStart, setControlGuidanceStart] = useState<number>(0.0);
  const [controlGuidanceEnd, setControlGuidanceEnd] = useState<number>(0.6);

  const showModal = () => setIsModalOpen(true);
  const handleOk = () => setIsModalOpen(false);
  const handleCancel = () => setIsModalOpen(false);

  const onAdd = (file: UploadFile) => {
    file.url = file.original || file.url;
    setControlnetImage(file);
  };

  const handleStyleToggle = (style: string) => {
    setStyles(prevStyles =>
      prevStyles.includes(style) ? prevStyles.filter(s => s !== style) : [...prevStyles, style]
    );
  };

  const getPhotoAction = (file: UploadFile) => {
    return [
      <Button style={{ width: '100%' }} type="primary" onClick={() => onAdd(file)} key="preview">
        Select
      </Button>,
    ];
  };

  const onGenerate = async (e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault();
    setLoadingGenerate(true);
    const data = {
      styles,
      image: controlnetImage?.url,
      planId,
      settings: {
        steps: +steps,
        control_scale: +controlScale,
        control_type: +controlType,
        guidance: +guidance,
        control_guidance_start: +controlGuidanceStart,
        control_guidance_end: +controlGuidanceEnd,
      },
    };

    try {
      const response = await axios({
        method: 'post',
        url: '/api/flux/generate-images',
        data,
      });
      console.debug(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingGenerate(false);
    }
  };

  const onAddPhoto = (url: string) => {
    const newPhoto: FluxPhoto = {
      uid: uuidv4(),
      name: url.split('/').pop() || 'image',
      status: 'done',
      url: url,
      original: url,
      isPublish: false,
      isPrimary: false,
      type: '',
      percent: 100,
      size: 0,
    };

    const newList = [...value, newPhoto];
    onChange?.(newList);
  };

  return (
    <>
      <Button type="primary" onClick={showModal}>
        Flux exterior
      </Button>
      <Modal width="90%" title="Flux" open={isModalOpen} onOk={handleOk} onCancel={handleCancel}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <Title level={4}>1. Select Exterior View Image (for ControlNet)</Title>
            <Text type="secondary">
              Choose an image for the exterior configuration. This will be used to generate a
              preview.
            </Text>
            <div style={{ marginTop: 16 }}>
              {value && value.length ? (
                <FileGrid fileList={value} getActions={getPhotoAction} hideMetadata={true} />
              ) : (
                <Text>No photos available.</Text>
              )}
            </div>
          </Col>
          {controlnetImage?.url && (
            <Col span={24}>
              <Title level={4}>2. Configure Details</Title>
              <Card
                hoverable
                bordered
                bodyStyle={{ padding: 0, margin: 0 }}
                cover={
                  <Image
                    src={
                      controlnetImage.url ||
                      (controlnetImage.originFileObj
                        ? URL.createObjectURL(controlnetImage.originFileObj)
                        : '')
                    }
                  />
                }
              />
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Col span={24}>
                  <Text>Steps {steps}</Text>
                  <Slider min={20} max={50} defaultValue={28} value={steps} onChange={setSteps} />
                </Col>
                <Col span={24}>
                  <Text>Guidance</Text>
                  <Input
                    type="number"
                    min={1}
                    max={10}
                    defaultValue={3.5}
                    step={0.1}
                    value={guidance}
                    onChange={e => setGuidance(Number(e.target.value))}
                  />
                </Col>
                <Col span={24}>
                  <Text>ControlNet Type </Text>
                  <Select value={controlType} onChange={setControlType}>
                    <Select.Option value={2}>Depth</Select.Option>
                    <Select.Option value={0}>Canny</Select.Option>
                  </Select>
                </Col>
                <Col span={24}>
                  <Text>Control scale</Text>
                  <Input
                    type="number"
                    min={0}
                    max={1}
                    defaultValue={0.6}
                    step={0.01}
                    value={controlScale}
                    onChange={e => setControlScale(Number(e.target.value))}
                  />
                </Col>
                <Col span={24}>
                  <Text>Control guidance start</Text>
                  <Input
                    type="number"
                    min={0}
                    max={1}
                    defaultValue={0.0}
                    step={0.01}
                    value={controlGuidanceStart}
                    onChange={e => setControlGuidanceStart(Number(e.target.value))}
                  />
                </Col>
                <Col span={24}>
                  <Text>Control guidance end</Text>
                  <Input
                    type="number"
                    min={0}
                    max={1}
                    defaultValue={0.0}
                    step={0.01}
                    value={controlGuidanceEnd}
                    onChange={e => setControlGuidanceEnd(Number(e.target.value))}
                  />
                </Col>
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                {stylesList.map(({ value, text }) => (
                  <Col span={6} key={value}>
                    <Card
                      hoverable
                      style={{
                        textAlign: 'center',
                        border: styles.includes(value) ? '2px solid #1890ff' : '1px solid #d9d9d9',
                      }}
                      onClick={() => handleStyleToggle(value)}>
                      <Checkbox
                        checked={styles.includes(value)}
                        onChange={() => handleStyleToggle(value)}>
                        {text}
                      </Checkbox>
                    </Card>
                  </Col>
                ))}
              </Row>
              <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                <Button type="primary" onClick={onGenerate} loading={loadingGenerate}>
                  Generate
                </Button>
              </Row>
            </Col>
          )}
          <FluxGenerations loadingGenerate={loadingGenerate} id={planId} onAdd={onAddPhoto} />
        </Row>
      </Modal>
    </>
  );
};

export default FluxExterior;
